import React, { useState } from "react";
import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import Checkbox from "../../../components/Checkbox/Checkbox";
import DatePick from "../../../components/DatePick/DatePick";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/TextArea/TextArea";

import { formatRunDate, parseRunDate, runOption, runOptions } from "./runFormFields";

import "./UKCRallyRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun | null;
}

const UKCRallyRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  const [rallyClass, setRallyClass] = useState(editRun?.class || "");
  const [score, setScore] = useState(editRun?.score?.toString() || "");
  const [resultStatus, setResultStatus] = useState(editRun?.result_status || Constants.run_result_status.qualified);
  const [trialDate, setTrialDate] = useState(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : ""
  );
  const [placement, setPlacement] = useState(editRun?.placement?.toString() || "");
  const [dogsDefeated, setDogsDefeated] = useState(editRun?.dogs_defeated?.toString() || "");
  const [awards, setAwards] = useState<string[]>(editRun?.awards || []);
  const [judgeNumber, setJudgeNumber] = useState(editRun?.judge_number || "");
  const [judgeName, setJudgeName] = useState(editRun?.judge_name || "");
  const [eventName, setEventName] = useState(editRun?.event_name || "");
  const [city, setCity] = useState(editRun?.location?.city || "");
  const [state, setState] = useState(editRun?.location?.state || "");
  const [notes, setNotes] = useState(editRun?.notes || "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);
    
    const formData: any = {
      dogId: dog._id,
      rallyClass,
      score: parseFloat(score),
      resultStatus,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      placement: placement ? parseInt(placement) : undefined,
      dogsDefeated: dogsDefeated ? parseInt(dogsDefeated) : undefined,
      awards: awards.length > 0 ? awards : undefined,
      eventName: eventName || undefined,
      location: (city || state) ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      formData.run_id = editRun._id;
    }

    await onSubmit(formData);
  };

  const handleAwardToggle = (award: string) => {
    if (awards.includes(award)) {
      setAwards(awards.filter(a => a !== award));
    } else {
      setAwards([...awards, award]);
    }
  };

  return (
    <form className="ukcRallyRunForm" onSubmit={handleSubmit}>
      <h2>{editRun ? 'Edit' : 'Add'} UKC Rally Run</h2>
      <p className="dogName">For: {dog.call_name}</p>

      <div className="formGrid">
        <div className="formGroup">
          <Dropdown
            label="Class *"
            placeholder="Select class..."
            options={runOptions([
              { value: Constants.ukc_rally_class.level_1_a, label: "Level 1 A" },
              { value: Constants.ukc_rally_class.level_1_b, label: "Level 1 B" },
              { value: Constants.ukc_rally_class.level_2_a, label: "Level 2 A" },
              { value: Constants.ukc_rally_class.level_2_b, label: "Level 2 B" },
              { value: Constants.ukc_rally_class.level_3_a, label: "Level 3 A" },
              { value: Constants.ukc_rally_class.level_3_b, label: "Level 3 B" },
              { value: Constants.ukc_rally_class.masters_a, label: "Masters A" },
              { value: Constants.ukc_rally_class.masters_b, label: "Masters B" },
            ], "Select class...")}
            value={runOption(runOptions([
              { value: Constants.ukc_rally_class.level_1_a, label: "Level 1 A" },
              { value: Constants.ukc_rally_class.level_1_b, label: "Level 1 B" },
              { value: Constants.ukc_rally_class.level_2_a, label: "Level 2 A" },
              { value: Constants.ukc_rally_class.level_2_b, label: "Level 2 B" },
              { value: Constants.ukc_rally_class.level_3_a, label: "Level 3 A" },
              { value: Constants.ukc_rally_class.level_3_b, label: "Level 3 B" },
              { value: Constants.ukc_rally_class.masters_a, label: "Masters A" },
              { value: Constants.ukc_rally_class.masters_b, label: "Masters B" },
            ], "Select class..."), rallyClass)}
            onChange={(_e, option) => setRallyClass(option.id)}
          />
        </div>

        <div className="formGroup">
          <Input
            label="Score * (0-200)"
            id="score"
            type="number"
            min="0"
            max="200"
            step="0.5"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            required
          />
          <span className="fieldHelp">Half points allowed (e.g., 185.5)</span>
        </div>

        <div className="formGroup">
          <Dropdown
            label="Result *"
            placeholder="Select result"
            options={runOptions([
              { value: Constants.run_result_status.qualified, label: "Qualified" },
              { value: Constants.run_result_status.not_qualified, label: "Not Qualified" },
            ])}
            value={runOption(runOptions([
              { value: Constants.run_result_status.qualified, label: "Qualified" },
              { value: Constants.run_result_status.not_qualified, label: "Not Qualified" },
            ]), resultStatus)}
            onChange={(_e, option) => setResultStatus(option.id)}
          />
        </div>

        <div className="formGroup">
          <DatePick
            label="Trial Date *"
            placeholder="Select date"
            value={parseRunDate(trialDate)}
            onChange={(date: Date | null) => setTrialDate(formatRunDate(date))}
          />
        </div>

        <div className="formGroup">
          <Input
            label="Placement"
            id="placement"
            type="number"
            min="1"
            value={placement}
            onChange={(e) => setPlacement(e.target.value)}
            placeholder="1st, 2nd, etc."
          />
        </div>

        <div className="formGroup">
          <Input
            label="Dogs Defeated"
            id="dogsDefeated"
            type="number"
            min="0"
            value={dogsDefeated}
            onChange={(e) => setDogsDefeated(e.target.value)}
            placeholder="For championship points"
          />
        </div>
      </div>

      <div className="formSection">
        <h3>Awards (for Championship Points)</h3>
        <div className="checkboxGroup">
          <Checkbox
            id="ukc-rally-high-in-trial"
            label="High in Trial"
            value={awards.includes(Constants.ukc_obedience_award.high_in_trial)}
            onChange={() => handleAwardToggle(Constants.ukc_obedience_award.high_in_trial)}
          />
        </div>
      </div>

      <div className="formSection">
        <h3>Judge Information</h3>
        <div className="formGrid">
          <div className="formGroup">
            <Input
              label="Judge Number"
              id="judgeNumber"
              value={judgeNumber}
              onChange={(e) => setJudgeNumber(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="formGroup">
            <Input
              label="Judge Name"
              id="judgeName"
              value={judgeName}
              onChange={(e) => setJudgeName(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
      </div>

      <div className="formSection">
        <h3>Event Information</h3>
        <div className="formGroup">
          <Input
            label="Event Name"
            id="eventName"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="formGrid">
          <div className="formGroup">
            <Input
              label="City"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="formGroup">
            <Input
              label="State"
              id="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength={2}
              placeholder="CA"
            />
          </div>
        </div>
      </div>

      <div className="formSection">
        <TextArea
          className="runNotes"
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this run"
        />
      </div>

      <div className="formActions">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : (editRun ? "Update Run" : "Add Run")}
        </Button>
      </div>
    </form>
  );
};

export default UKCRallyRunForm;

